import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AsyncData } from '@model/common';
import { BookingApi, PutSeatMapsParams } from '@model/iceberg/service/booking';
import { SeatMapsResponse } from '@model/iceberg/service';
import { fetchBasket } from '@state/basket';
import { getErrorCode } from '@util/error';
import { clearResults, DealFinderResultsActions } from '@state/slices/deal-finder-results/dealFinderResultsSlice';
import { createListener } from '@state/store/createListener';

export enum SeatMapsActions {
  SLICE_NAME = '@SEATMAPS',
  FETCH_SEATMAPS = '@SEATMAPS/FETCH_SEATMAPS',
  CLEAR_SEATMAPS = '@SEATMAPS/clearSeatMaps',
  RECEIVE_SEATMAPS_SUCCESS = '@SEATMAPS/RECEIVE_SEATMAPS_SUCCESS',
  RECEIVE_SEATMAPS_FAILURE = '@SEATMAPS/RECEIVE_SEATMAPS_FAILURE',
  SET_UNCONFIRMED_SEATS = '@SEATMAPS/setUnconfirmedSeats',
  PUT_SEATMAPS = '@SEATMAPS/PUT_SEATMAPS'
}

export interface SeatMapsState extends AsyncData<SeatMapsResponse | null> {
  hasUnconfirmed: boolean;
}

export const INITIAL_SEATMAPS_STATE: SeatMapsState = {
  data: null,
  hasUnconfirmed: false,
  loading: false,
  error: null
};

export const fetchSeatMaps = createAsyncThunk<SeatMapsResponse, string, { rejectValue: string }>(
  SeatMapsActions.FETCH_SEATMAPS,
  async (searchToken: string, { rejectWithValue }) => {
    try {
      const bookingApi = new BookingApi();
      return await bookingApi.fetchSeatMaps(searchToken);
    } catch (error: any) {
      return rejectWithValue(getErrorCode(error));
    }
  }
);

export const putSeatMaps = createAsyncThunk<void, PutSeatMapsParams, { rejectValue: string }>(
  SeatMapsActions.PUT_SEATMAPS,
  async (payload: PutSeatMapsParams, { dispatch, rejectWithValue }) => {
    try {
      const bookingApi = new BookingApi();
      const { searchToken } = payload;

      await bookingApi.putSeatMaps(payload);

      await dispatch(fetchSeatMaps(searchToken));
      dispatch(fetchBasket(searchToken));
    } catch (error: any) {
      return rejectWithValue(getErrorCode(error));
    }
  }
);

export const seatMapsSlice = createSlice({
  name: SeatMapsActions.SLICE_NAME,
  initialState: INITIAL_SEATMAPS_STATE,
  reducers: {
    clearSeatMaps: () => {
      return INITIAL_SEATMAPS_STATE;
    },
    setUnconfirmedSeats: (state, action: PayloadAction<boolean>) => {
      state.hasUnconfirmed = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSeatMaps.pending, (state) => {
        state.loading = true;
        state.hasUnconfirmed = false;
      })
      .addCase(fetchSeatMaps.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchSeatMaps.rejected, (state, action) => {
        state.loading = false;
        state.data = null;
        state.error = action.payload || 'Unknown error occurred';
      })

      .addCase(putSeatMaps.pending, (state) => {
        state.loading = true;
        state.hasUnconfirmed = false;
      })
      .addCase(putSeatMaps.fulfilled, (_state) => {})
      .addCase(putSeatMaps.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update seat maps';
      })
      .addCase(DealFinderResultsActions.CLEAR_RESULTS, () => INITIAL_SEATMAPS_STATE);
  }
});

export const setupSeatMapsListeners = () => {
  createListener([clearResults], async (_action, listenerApi) => {
    listenerApi.dispatch(clearSeatMaps());
  });
};

export const { clearSeatMaps, setUnconfirmedSeats } = seatMapsSlice.actions;
export const seatMapsReducer = seatMapsSlice.reducer;
